export const DEV_PLAN_PRICES = {
	lite: 29,
	pro: 79,
	max: 179,
} as const;

export type DevPlanTier = keyof typeof DEV_PLAN_PRICES;

// Dev plans are billed monthly. The "annual" value is retained only so the
// schema/types can still describe legacy annual subscriptions that predate the
// removal of the yearly option; no new annual subscriptions are created.
export type DevPlanCycle = "monthly" | "annual";

export function getDevPlanCreditsLimit(tier: DevPlanTier): number {
	const multiplier = parseFloat(process.env.DEV_PLAN_CREDITS_MULTIPLIER ?? "3");
	return DEV_PLAN_PRICES[tier] * multiplier;
}

/**
 * Weekly fair-use allowance for premium-category models per tier.
 * Premium models (frontier flagships) are subject to this weekly cap in
 * addition to the monthly credit allowance.
 */
export const DEV_PLAN_PREMIUM_WEEKLY_LIMITS: Record<DevPlanTier, number> = {
	lite: 10,
	pro: 50,
	max: 140,
};

export function getDevPlanPremiumWeeklyLimit(tier: DevPlanTier): number {
	return DEV_PLAN_PREMIUM_WEEKLY_LIMITS[tier];
}

export const DEV_PLAN_PREMIUM_WEEK_LENGTH_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Returns true when the stored premium-week start is older than the rolling
 * 7-day window (or absent), meaning the premium usage counter should be
 * reset before the next deduction or check.
 */
export function isPremiumWeekExpired(
	weekStart: Date | null | undefined,
	now: Date = new Date(),
): boolean {
	if (!weekStart) {
		return true;
	}
	return (
		now.getTime() - new Date(weekStart).getTime() >=
		DEV_PLAN_PREMIUM_WEEK_LENGTH_MS
	);
}

/**
 * Returns the remaining premium allowance for the current weekly window.
 * If the stored week has expired, the full per-tier limit is available.
 */
export function getRemainingPremiumWeeklyAllowance(
	tier: DevPlanTier,
	creditsUsed: string | number | null | undefined,
	weekStart: Date | null | undefined,
	now: Date = new Date(),
): number {
	const limit = getDevPlanPremiumWeeklyLimit(tier);
	if (isPremiumWeekExpired(weekStart, now)) {
		return limit;
	}
	const used =
		typeof creditsUsed === "string"
			? parseFloat(creditsUsed)
			: (creditsUsed ?? 0);
	return Math.max(0, limit - used);
}

/**
 * Prorated credit delta for a mid-cycle tier change.
 *
 * Credits track prorated dollars: changing tier part-way through a billing
 * period grants (upgrade) or removes (downgrade) only the difference in the
 * tiers' credit allotments scaled by the fraction of the period that remains —
 * mirroring the prorated amount Stripe charges or credits back. Returns a
 * positive number for upgrades and a negative number for downgrades.
 */
export function getProratedCreditDelta(
	fromTier: DevPlanTier,
	toTier: DevPlanTier,
	remainingFraction: number,
): number {
	const normalized = Number.isFinite(remainingFraction) ? remainingFraction : 0;
	const clamped = Math.min(1, Math.max(0, normalized));
	const delta =
		getDevPlanCreditsLimit(toTier) - getDevPlanCreditsLimit(fromTier);
	return delta * clamped;
}
